import { useState } from "react";
import { Phone, Mail, MapPin, Facebook, Instagram, Send } from "lucide-react";
import { motion } from "framer-motion";

export default function Footer() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Dopyt od ${form.name}`);
    const body = encodeURIComponent(
      `Meno: ${form.name}\nEmail: ${form.email}\n\nSpráva:\n${form.message}`
    );
    window.location.href = `mailto:danka.vysoka@gmail.com?subject=${subject}&body=${body}`;
  };

  return (
    <footer
      id="kontakt"
      className="relative overflow-hidden"
      style={{
        background: "#252540",
        borderTop: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      {/* Massive background watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <span
          className="font-black select-none leading-none whitespace-nowrap"
          style={{
            fontFamily: "Outfit, sans-serif",
            fontSize: "18vw",
            color: "rgba(255,255,255,0.025)",
          }}
        >
          DANKA FITNESS
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative z-10">
        {/* Big CTA Headline */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <h2
            data-testid="footer-heading"
          className="font-black uppercase tracking-tighter text-white leading-none mb-4"
            style={{ fontFamily: "Outfit, sans-serif", fontSize: "clamp(2.5rem, 7vw, 6rem)" }}
          >
            POSUŇ SVOJE{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #F72585, #7209B7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              LIMITY
            </span>
          </h2>
          <p
            className="text-lg text-zinc-400 max-w-xl mx-auto"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Napíš mi a spoločne nastavíme tréningový plán šitý na mieru tvojim
            cieľom.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3
              className="text-xl font-bold text-white mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Napíš mi
            </h3>
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="space-y-4"
            >
              <input
                type="text"
                placeholder="Tvoje meno"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
                data-testid="contact-name-input"
                className="w-full rounded-xl px-4 py-3 text-white text-sm focus:outline-none transition-colors"
                style={{
                  background: "#1C1C2E",
                  border: "1px solid rgba(255,255,255,0.1)",
                  fontFamily: "Manrope, sans-serif",
                }}
                onFocus={(e) =>
                  (e.target.style.border = "1px solid #F72585")
                }
                onBlur={(e) =>
                  (e.target.style.border = "1px solid rgba(255,255,255,0.1)")
                }
              />
              <input
                type="email"
                placeholder="Tvoj email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                data-testid="contact-email-input"
                className="w-full rounded-xl px-4 py-3 text-white text-sm focus:outline-none transition-colors"
                style={{
                  background: "#1C1C2E",
                  border: "1px solid rgba(255,255,255,0.1)",
                  fontFamily: "Manrope, sans-serif",
                }}
                onFocus={(e) =>
                  (e.target.style.border = "1px solid #F72585")
                }
                onBlur={(e) =>
                  (e.target.style.border = "1px solid rgba(255,255,255,0.1)")
                }
              />
              <textarea
                placeholder="Tvoja správa..."
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                required
                rows={5}
                data-testid="contact-message-input"
                className="w-full rounded-xl px-4 py-3 text-white text-sm focus:outline-none resize-none transition-colors"
                style={{
                  background: "#1C1C2E",
                  border: "1px solid rgba(255,255,255,0.1)",
                  fontFamily: "Manrope, sans-serif",
                }}
                onFocus={(e) =>
                  (e.target.style.border = "1px solid #F72585")
                }
                onBlur={(e) =>
                  (e.target.style.border = "1px solid rgba(255,255,255,0.1)")
                }
              />
              <button
                type="submit"
                data-testid="contact-submit-button"
                className="w-full py-4 text-white font-bold rounded-xl transition-all duration-300 hover:scale-[1.02] flex items-center justify-center gap-2 text-sm"
                style={{
                  background: "#F72585",
                  fontFamily: "Outfit, sans-serif",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#E01A4F";
                  e.currentTarget.style.boxShadow =
                    "0 0 30px rgba(247,37,133,0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#F72585";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <Send size={16} />
                Odoslať správu
              </button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col justify-between"
          >
            <div>
              <h3
                className="text-xl font-bold text-white mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                Kontakt
              </h3>
              <div className="space-y-4 mb-8">
                <a
                  href="tel:+421948848759"
                  data-testid="footer-phone"
                  className="flex items-center gap-4 group"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{ background: "rgba(247,37,133,0.1)" }}
                  >
                    <Phone size={16} style={{ color: "#F72585" }} />
                  </div>
                  <span className="text-zinc-300 group-hover:text-white transition-colors text-sm">
                    +421 948 848 759
                  </span>
                </a>
                <a
                  href="mailto:danka.vysoka@gmail.com"
                  data-testid="footer-email"
                  className="flex items-center gap-4 group"
                >
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                    style={{ background: "rgba(247,37,133,0.1)" }}
                  >
                    <Mail size={16} style={{ color: "#F72585" }} />
                  </div>
                  <span className="text-zinc-300 group-hover:text-white transition-colors text-sm">
                    danka.vysoka@gmail.com
                  </span>
                </a>
                <div className="flex items-center gap-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(247,37,133,0.1)" }}
                  >
                    <MapPin size={16} style={{ color: "#F72585" }} />
                  </div>
                  <span className="text-zinc-300 text-sm">
                    City Fitness Ruzinov, Bratislava
                  </span>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h4 className="text-xs font-semibold text-zinc-500 mb-4 uppercase tracking-widest">
                  Sociálne siete
                </h4>
                <div className="flex gap-3">
                  <a
                    href="https://www.facebook.com/dana.vysoka/"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-facebook"
                    className="flex items-center gap-2 rounded-xl px-4 py-2 text-zinc-400 transition-all text-sm"
                    style={{
                      background: "#1C1C2E",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#F72585";
                      e.currentTarget.style.borderColor =
                        "rgba(247,37,133,0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "rgb(161,161,170)";
                      e.currentTarget.style.borderColor =
                        "rgba(255,255,255,0.1)";
                    }}
                  >
                    <Facebook size={18} />
                    <span className="font-medium">Facebook</span>
                  </a>
                  <a
                    href="https://www.instagram.com/sisticka6"
                    target="_blank"
                    rel="noreferrer"
                    data-testid="footer-instagram"
                    className="flex items-center gap-2 rounded-xl px-4 py-2 text-zinc-400 transition-all text-sm"
                    style={{
                      background: "#1C1C2E",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#F72585";
                      e.currentTarget.style.borderColor =
                        "rgba(247,37,133,0.5)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "rgb(161,161,170)";
                      e.currentTarget.style.borderColor =
                        "rgba(255,255,255,0.1)";
                    }}
                  >
                    <Instagram size={18} />
                    <span className="font-medium">Instagram</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Google Maps */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12"
        >
          <h3
            className="text-xl font-bold text-white mb-5"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Kde nás nájdete
          </h3>
          <p className="text-sm text-zinc-400 mb-5">
            City Fitness Ruzinov · Mliekárenská 10, 821 09 Bratislava-Ružinov
          </p>
          <div
            className="relative rounded-2xl overflow-hidden"
            style={{ border: "1px solid rgba(255,255,255,0.08)" }}
            data-testid="footer-map"
          >
            {/* Pink top accent line */}
            <div
              className="absolute top-0 left-0 right-0 h-1 z-10"
              style={{ background: "linear-gradient(90deg, #F72585, #7209B7)" }}
            />
            <iframe
              title="City Fitness Ruzinov"
              src="https://maps.google.com/maps?q=City+Fitness+Ruzinov+Mliekarenska+10+Bratislava&t=&z=16&ie=UTF8&iwloc=&output=embed"
              width="100%"
              height="280"
              style={{ border: 0, display: "block", filter: "invert(5%) hue-rotate(200deg) saturate(0.85)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </motion.div>

        {/* Copyright strip */}
        <div
          className="mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}
        >
          <div
            className="text-lg font-black"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            <span className="text-white">Danka </span>
            <span style={{ color: "#F72585" }}>Fitness</span>
          </div>
          <p className="text-xs text-zinc-600 text-center">
            © {new Date().getFullYear()} Danka Vysoká · Certifikovaná fitness trénerka · Bratislava
          </p>
        </div>
      </div>
    </footer>
  );
}
